import { Suspense, lazy, useEffect, useState } from 'react';
import { abfrageSetzen, abfrageWert } from '../adresse';
import { Link, useNavigate } from 'react-router';
import { useIch } from '../App';
import { api, abmeldenUrl, type Kunde, type ProjektKurz } from '../api';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { Glocke } from './Glocke';
import { Benutzermenue } from './Benutzermenue';
import { toast } from './toast';
import { MeineAufgaben } from './MeineAufgaben';
import { MerkStern } from './MerkStern';
import { frage, archivierenBestaetigen } from './frage';
import { menueOeffnen, adresse, kopieren, neuerTab } from './Kontextmenue';
import { projektkachelMenue } from '../kontextmenue';
// Verwaltungsdialoge (nur Superadmins/Admins) erst bei Bedarf laden – hält das Haupt-Bundle klein
const verwaltung = () => import('./Verwaltung');
const KundeDialog = lazy(() => verwaltung().then((m) => ({ default: m.KundeDialog })));
const KundeLoeschenDialog = lazy(() => verwaltung().then((m) => ({ default: m.KundeLoeschenDialog })));
const ProjektDialog = lazy(() => verwaltung().then((m) => ({ default: m.ProjektDialog })));
const SicherungDialog = lazy(() => verwaltung().then((m) => ({ default: m.SicherungDialog })));
const SuperadminsDialog = lazy(() => verwaltung().then((m) => ({ default: m.SuperadminsDialog })));
const BenutzerDialog = lazy(() => verwaltung().then((m) => ({ default: m.BenutzerDialog })));
const TicketsystemDialog = lazy(() => verwaltung().then((m) => ({ default: m.TicketsystemDialog })));
const kundeExportieren = async (k: Kunde) => (await verwaltung()).kundeExportieren(k);

const ROLLE: Record<string, string> = { admin: 'Admin', verantwortlich: 'Prozessverantwortlich', mitwirkend: 'Mitwirkend', lesend: 'Lesend' };

type Dialog = null | { art: 'kunde-neu' } | { art: 'kunde'; kunde: Kunde } | { art: 'projekt'; kunde: Kunde } | { art: 'kunde-loeschen'; kunde: Kunde } | { art: 'superadmins' } | { art: 'benutzer' } | { art: 'ticketsystem' } |{ art: 'sicherung'; projekt: ProjektKurz };

/** Startseite: Kunden und Projekte, die die angemeldete Person sehen darf; Superadmins verwalten sie hier. */
export function Start() {
  const { ich, neuLaden } = useIch();
  const nav = useNavigate();
  // Filter und „archivierte zeigen“ stehen in der Adresse (?q=…&archiviert=1), damit ein Lesezeichen sie behält
  const [filter, setFilter] = useState(() => new URLSearchParams(location.search).get('q') ?? '');
  const [dialog, setDialog] = useState<Dialog>(null);
  // Archivierte Projekte (nur lesbar) sind standardmäßig ausgeblendet
  const [archivierte, setArchivierte] = useState(() => abfrageWert('archiviert', ['1']) === '1');
  // Favoriten (Stern, persönlich): stehen vorn; „Nur Favoriten“ blendet alles andere aus (?favoriten=1)
  const [nurFavoriten, setNurFavoriten] = useState(() => abfrageWert('favoriten', ['1']) === '1');
  // sofort umgeschaltete Sterne, bis die Übersicht neu geladen ist
  const [sterne, setSterne] = useState<Record<string, boolean>>({});
  useEffect(() => { setSterne({}); }, [ich]);
  useEffect(() => { abfrageSetzen({ q: filter.trim() ? filter : null, archiviert: archivierte ? '1' : null, favoriten: nurFavoriten ? '1' : null }); }, [filter, archivierte, nurFavoriten]);
  if (!ich) return null;
  const istFavorit = (art: 'kunde' | 'projekt', x: { id: string; favorit?: boolean }) => sterne[art + ':' + x.id] ?? !!x.favorit;
  const favoritUmschalten = async (art: 'kunde' | 'projekt', x: { id: string; favorit?: boolean }) => {
    const neu = !istFavorit(art, x);
    setSterne((s) => ({ ...s, [art + ':' + x.id]: neu }));
    try { await api.favoritSetzen(art, x.id, neu); neuLaden(); }
    catch (e) { setSterne((s) => ({ ...s, [art + ':' + x.id]: !neu })); toast('Favorit nicht gespeichert: ' + (e as Error).message, true); }
  };
  const STERN_KUNDE: [string, string] = ['Kein Favorit mehr', 'Kunde als Favorit markieren (nur für Sie sichtbar)'];
  const STERN_PROJEKT: [string, string] = ['Kein Favorit mehr', 'Projekt als Favorit markieren (nur für Sie sichtbar)'];
  const sa = ich.superadmin;
  const q = filter.trim().toLowerCase();
  const anzahlArchiviert = ich.kunden.reduce((n, k) => n + k.projekte.filter((p) => p.archiviertAm).length, 0);
  const anzahlFavoriten = ich.kunden.reduce((n, k) => n + Number(istFavorit('kunde', k)) + k.projekte.filter((p) => istFavorit('projekt', p)).length, 0);
  // Favoriten zuerst (Kunden wie Projekte), sonst die Reihenfolge des Servers (nach Name bzw. Titel)
  const vorn = <T,>(liste: T[], fav: (x: T) => boolean) => [...liste.filter(fav), ...liste.filter((x) => !fav(x))];
  const kunden = vorn(ich.kunden
    .map((k) => ({ ...k, projekte: vorn(k.projekte.filter((p) => (archivierte || !p.archiviertAm)
      && (!q || (k.name + ' ' + p.titel + ' ' + (p.untertitel ?? '')).toLowerCase().includes(q))
      // „Nur Favoriten“: markierte Projekte – und alle Projekte eines markierten Kunden
      && (!nurFavoriten || istFavorit('projekt', p) || istFavorit('kunde', k))), (p) => istFavorit('projekt', p)) }))
    .filter((k) => k.projekte.length > 0 || (sa && (!q || k.name.toLowerCase().includes(q)) && (!nurFavoriten || istFavorit('kunde', k)))),
    (k) => istFavorit('kunde', k) || k.projekte.some((p) => istFavorit('projekt', p)));

  const projektLoeschen = async (p: ProjektKurz) => {
    if (!(await frage(`Projekt „${p.titel}“ mit allen Daten, Mitgliedern und Kommentaren endgültig löschen?`, { titel: 'Projekt löschen', ja: 'Endgültig löschen', gefahr: true }))) return;
    try {
      await api.projektLoeschen(p.id);
      toast(`Projekt „${p.titel}“ gelöscht.`);
      neuLaden();
    } catch (e) {
      toast((e as Error).message, true);
    }
  };
  const kunde = (id: string) => ich.kunden.find((k) => k.id === id)!;
  // Archivieren/Wiederherstellen wie in der Kopfleiste des Projekts (nur Admins des Projekts und Superadmins)
  const archivieren = async (p: ProjektKurz) => {
    if (!(await archivierenBestaetigen(p.titel))) return;
    try { await api.projektArchivieren(p.id); toast('Projekt archiviert – nur lesbar.'); neuLaden(); }
    catch (e) { toast((e as Error).message, true); }
  };
  const wiederherstellen = async (p: ProjektKurz) => {
    try { await api.projektWiederherstellen(p.id); toast('Projekt wiederhergestellt.'); neuLaden(); }
    catch (e) { toast((e as Error).message, true); }
  };
  const kachelMenue = (p: ProjektKurz) => {
    const pfad = `/p/${encodeURIComponent(p.id)}`;
    return projektkachelMenue({ archiviert: !!p.archiviertAm }, { istAdmin: p.rolle === 'admin' || sa, superadmin: sa }, {
      oeffnen: () => nav(pfad),
      neuerTab: () => neuerTab(adresse(pfad)),
      linkKopieren: () => kopieren(adresse(pfad)),
      archivieren: () => archivieren(p),
      wiederherstellen: () => wiederherstellen(p),
      sicherung: () => setDialog({ art: 'sicherung', projekt: p }),
      loeschen: () => projektLoeschen(p),
    });
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <img className="logo-iwm-light" src="/iwm_logo.png" alt="IWM" />
          <img className="logo-iwm-dark" src="/iwm_logo_dunkel.png" alt="IWM" />
        </div>
        <div className="titles"><div className="t">Projects</div></div>
        <div className="tools">
          <Glocke />
          <Benutzermenue ich={ich} />
        </div>
      </header>
      <div className="main">
        <section className="view">
          <div className="landkarte" data-testid="start">
            <div className="head start-kopf">
              <div>
                <h1>Kunden &amp; Projekte</h1>
                <input className="inp" style={{ maxWidth: 420 }} placeholder="Filtern: Kunde, Projekt …" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="projekt-filter" />
                {(anzahlFavoriten > 0 || nurFavoriten) && (
                  <div>
                    <label className="archiv-schalter" data-testid="nur-favoriten">
                      <input type="checkbox" checked={nurFavoriten} onChange={(e) => setNurFavoriten(e.target.checked)} />
                      Nur Favoriten ({anzahlFavoriten})
                    </label>
                  </div>
                )}
                {anzahlArchiviert > 0 && (
                  <div>
                    <label className="archiv-schalter" data-testid="archivierte-anzeigen">
                      <input type="checkbox" checked={archivierte} onChange={(e) => setArchivierte(e.target.checked)} />
                      Archivierte anzeigen ({anzahlArchiviert})
                    </label>
                  </div>
                )}
              </div>
              {sa && (
                <div className="facts" data-testid="superadmin-leiste">
                  <div className="lab">Superadmin</div>
                  <div className="verwaltung-leiste">
                    <button className="btn small" onClick={() => setDialog({ art: 'kunde-neu' })} data-testid="kunde-neu"><Icon name="plus" /> Neuer Kunde</button>
                    <button className="btn small" onClick={() => setDialog({ art: 'superadmins' })} data-testid="superadmins"><Icon name="gear" /> Superadmins …</button>
                    <button className="btn small" onClick={() => setDialog({ art: 'benutzer' })} data-testid="benutzer"><Icon name="users" /> Benutzer …</button>
                    <button className="btn small" onClick={() => setDialog({ art: 'ticketsystem' })} data-testid="ticketsystem"><Icon name="link" /> Ticketsystem …</button>
                  </div>
                </div>
              )}
            </div>
            {ich.kunden.some((k) => k.projekte.length > 0) && <MeineAufgaben />}
            {kunden.length === 0 && (
              <div className="empty-state">
                {ich.kunden.length === 0 && sa
                  ? 'Noch keine Kunden – mit „Neuer Kunde“ beginnen.'
                  : ich.kunden.length === 0
                  ? 'Sie sind noch keinem Projekt zugeordnet. Bitte wenden Sie sich an die Person, die Sie eingeladen hat.'
                  : nurFavoriten && !q
                  ? 'Keine Favoriten – mit dem Stern an einem Kunden oder Projekt markieren Sie einen.'
                  : !archivierte && anzahlArchiviert > 0 && !q
                  ? 'Keine aktiven Projekte – archivierte Projekte zeigt „Archivierte anzeigen“.'
                  : 'Keine Treffer.'}
              </div>
            )}
            {kunden.map((k) => (
              <div key={k.id} style={{ maxWidth: '95vw', margin: '0 auto 22px' }} data-testid={'kunde-' + k.id}>
                <div className="kunde-kopf">
                  <h2>{k.logo && <img src={k.logo} alt="" style={{ height: 30 }} />}{k.name}
                    <MerkStern gemerkt={istFavorit('kunde', k)} onUmschalten={() => void favoritUmschalten('kunde', k)} titel={STERN_KUNDE} testid={'kunde-favorit-' + k.id} /></h2>
                  {sa && (
                    <div className="aktionen">
                      <button className="btn small" onClick={() => setDialog({ art: 'projekt', kunde: kunde(k.id) })} data-testid={'projekt-neu-' + k.id}><Icon name="plus" /> Neues Projekt</button>
                      <button className="btn small" onClick={() => setDialog({ art: 'kunde', kunde: kunde(k.id) })} title="Name, Logo und checkITeasy-Kunde" data-testid={'kunde-bearbeiten-' + k.id}><Icon name="edit" /> Bearbeiten</button>
                      <button className="btn small" onClick={() => void kundeExportieren(kunde(k.id))} data-testid={'kunde-export-' + k.id}><Icon name="download" /> Exportieren</button>
                      <button className="btn small danger" onClick={() => setDialog({ art: 'kunde-loeschen', kunde: kunde(k.id) })} data-testid={'kunde-loeschen-' + k.id}><Icon name="trash" /> Löschen …</button>
                    </div>
                  )}
                </div>
                <div className="tiles" style={{ marginTop: 10 }}>
                  {k.projekte.map((p) => (
                    <div key={p.id} className="tile-wrap" onContextMenu={(e) => menueOeffnen(e, kachelMenue(p))}>
                      <Link to={`/p/${encodeURIComponent(p.id)}`} className={'tile' + (p.archiviertAm ? ' archiviert' : '')} data-testid={'projekt-' + p.id} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="kicker">{ROLLE[p.rolle] ?? p.rolle}{p.archiviertAm ? ' · Archiviert' : ''}</div>
                        <h3>{p.titel}<MerkStern gemerkt={istFavorit('projekt', p)} onUmschalten={() => void favoritUmschalten('projekt', p)} titel={STERN_PROJEKT} testid={'projekt-favorit-' + p.id} /></h3>
                        {p.untertitel && <p>{p.untertitel}</p>}
                        <div className="hint">Version {p.version || '–'}{p.stand ? ' · Stand ' + new Date(p.stand).toLocaleDateString('de-DE') : ''}</div>
                        {p.loeschenAm && <div className="hint" data-testid={'projekt-loeschung-' + p.id}>Nur lesbar · wird am {new Date(p.loeschenAm).toLocaleDateString('de-DE')} gelöscht</div>}
                      </Link>
                      {sa && <button className="btn icon small tile-loeschen" style={{ right: 40 }} title="Auf Sicherung zurücksetzen …" onClick={() => setDialog({ art: 'sicherung', projekt: p })} data-testid={'projekt-sicherung-' + p.id}><Icon name="undo" /></button>}
                      {sa && <button className="btn icon small tile-loeschen" title="Projekt löschen" onClick={() => void projektLoeschen(p)} data-testid={'projekt-loeschen-' + p.id}><Icon name="trash" /></button>}
                    </div>
                  ))}
                  {k.projekte.length === 0 && <div className="hint">{kunde(k.id).projekte.some((p) => p.archiviertAm) && !q ? 'Nur archivierte Projekte.' : 'Noch keine Projekte.'}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Suspense fallback={null}>
      {dialog?.art === 'kunde-neu' && <KundeDialog onClose={() => setDialog(null)} onFertig={neuLaden} />}
      {dialog?.art === 'kunde' && <KundeDialog kunde={dialog.kunde} onClose={() => setDialog(null)} onFertig={neuLaden} />}
      {dialog?.art === 'kunde-loeschen' && <KundeLoeschenDialog kunde={dialog.kunde} onClose={() => setDialog(null)} onFertig={neuLaden} />}
      {dialog?.art === 'superadmins' && <SuperadminsDialog ich={ich} onClose={() => setDialog(null)} />}
      {dialog?.art === 'sicherung' && <SicherungDialog projektId={dialog.projekt.id} titel={dialog.projekt.titel} onClose={() => setDialog(null)} onFertig={neuLaden} />}
      {dialog?.art === 'ticketsystem' && <TicketsystemDialog onClose={() => setDialog(null)} />}
      {dialog?.art === 'benutzer' &&<BenutzerDialog ich={ich} onClose={() => setDialog(null)} onFertig={neuLaden} />}
      {dialog?.art === 'projekt' && (
        <ProjektDialog ich={ich} kunde={dialog.kunde} onClose={() => setDialog(null)}
          onFertig={(id, ziel) => { setDialog(null); neuLaden(); nav('/p/' + encodeURIComponent(id) + (ziel ?? '')); }} />
      )}
      </Suspense>
    </div>
  );
}
